import { useState, useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { processActionConfirmations, CONFIRMATION_DISMISS_TIMEOUT } from "@/utils/actionConfirmations";
import { authFetch, newRequestId } from "@/lib/authFetch";
import { pickFirstSentenceForPrefetch } from "@/hooks/_voiceUtils";
import { shouldPreferVanilla } from "@/utils/shortCircuitDetector";
// TELEMETRIA-S1 — trace events de chat (send, network_total, first_render,
// stream_first_delta, stream_done). hashText pra nunca expor plain.
import trace, { hashText } from "@/services/executionTrace";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";
const STORAGE_KEY = (uid) => `nexo_chat_${uid || "default"}`;
const LEGACY_STORAGE_KEY = (uid) => `jarvis_chat_${uid || "default"}`;
// BUGFIX-CHAT-HISTORY: cap localStorage cresce até 200 (era 30). Backend
// retorna até 200 nos últimos 30 dias; cache local espelha pra mostrar
// instantâneo no reload sem esperar fetch.
const HISTORY_CACHE_CAP = 200;

// SMART-AUTOMATIONS Etapa 7 — toast com Undo após auto_setup disparar.
// Backend já criou tasks/notes/reminders; user tem 30s pra reverter.
function _showAutoSetupToast(autoSetup) {
  const { project_id, project_title, items_count } = autoSetup;
  toast.success(`Projeto "${project_title || "configurado"}" pronto`, {
    description: `${items_count} itens criados automaticamente. Toque "Desfazer" se mudou de ideia.`,
    duration: 30000,
    action: {
      label: "Desfazer",
      onClick: async () => {
        try {
          const r = await authFetch(`${API_URL}/api/assistant/undo_auto_setup`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ project_id }),
          });
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          const body = await r.json();
          toast.success(`Desfeito: ${body.reverted ?? 0} itens revertidos.`);
        } catch (err) {
          console.error("undo_auto_setup falhou:", err);
          toast.error("Não consegui desfazer agora.");
        }
      },
    },
  });
}

/**
 * useJarvisChat — manages chat messages, sending, confirmations, session, and persistence.
 */
export default function useJarvisChat({
  userId, sessionId, voiceEnabled, speakResponse, fetchDashboardData,
  // BUGFIX-LATENCY-V3 — when active, useJarvisChat forces streaming
  // (`/assistant/message/stream`) and dispatches TTS for the first
  // complete sentence as soon as it arrives, queuing the remainder for
  // chained playback. Cuts perceived latency to first audio frame.
  voiceConversationActive = false,
}) {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // loadingLabel: "Processando..." → "Pensando..." (3s) → "Ainda tô aqui, só um instante..." (15s)
  const [loadingLabel, setLoadingLabel] = useState("Processando...");
  const [inputText, setInputText] = useState("");
  const [confirmations, setConfirmations] = useState([]);
  // BUG-A FIX: gate per-userId em vez de boolean global. O bug:
  // useEffect com `if (initialLoadDone.current) return` ANTES do
  // `if (!userId) return` fechava o gate na primeira render (auth
  // ainda resolvendo, userId=null), e no re-run com userId definido
  // o gate já estava fechado → load nunca acontecia → user perdia
  // histórico após reload se auth fosse async. Now per-userId pra
  // re-disparar quando user log-in/troca de conta.
  const initialLoadDone = useRef(null);
  const loadingTimersRef = useRef([]);

  // Load chat history on mount
  useEffect(() => {
    // Sem userId (não logado), chat fica em memória — evita escrever em
    // "default_user" (mina silenciosa que mistura sessões anônimas).
    if (!userId) return;
    if (initialLoadDone.current === userId) return;
    initialLoadDone.current = userId;

    // Migrate legacy jarvis_chat_${uid} → nexo_chat_${uid} (one-time per user)
    try {
      const legacy = localStorage.getItem(LEGACY_STORAGE_KEY(userId));
      if (legacy !== null && localStorage.getItem(STORAGE_KEY(userId)) === null) {
        localStorage.setItem(STORAGE_KEY(userId), legacy);
        localStorage.removeItem(LEGACY_STORAGE_KEY(userId));
        console.log("[Nexo] Migrated legacy localStorage key:", LEGACY_STORAGE_KEY(userId), "→", STORAGE_KEY(userId));
      }
    } catch { /* ignore */ }

    // 1. Instant load from localStorage (cap 200 — alinha com backend)
    try {
      const stored = localStorage.getItem(STORAGE_KEY(userId));
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed.slice(-HISTORY_CACHE_CAP));
        }
      }
    } catch { /* ignore */ }

    // 2. Load from backend (authoritative, async). BUGFIX-CHAT-HISTORY:
    // limit bumpado de 30 → 200 e since_days=30 (alinhado com backend).
    // Antes user que passasse de 30 msgs perdia antigas no reload.
    (async () => {
      try {
        const res = await authFetch(
          `${API_URL}/api/chat-history?user_id=${encodeURIComponent(userId)}&limit=200&since_days=30`,
        );
        if (res.ok) {
          const data = await res.json();
          if (data.messages?.length > 0) {
            const mapped = data.messages.map(m => ({
              text: m.text,
              isUser: m.isUser,
              timestamp: m.timestamp,
              actions: m.actions || [],
              suggestions: m.suggestions || [],
              datacard: m.datacard || null,
            }));
            setMessages(mapped);
            localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(mapped));
          }
        } else {
          console.error(`[Nexo] /chat-history GET falhou: ${res.status}`);
        }
      } catch (err) {
        console.error("[Nexo] /chat-history GET erro de rede:", err?.message || err);
      }
    })();
  }, [userId]);

  // Save to localStorage on message changes — só com userId logado.
  useEffect(() => {
    if (!userId || messages.length === 0) return;
    try {
      localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(messages.slice(-HISTORY_CACHE_CAP)));
    } catch { /* ignore */ }
  }, [messages, userId]);

  // Save message to backend. Não é mais silencioso — falhas viram console.error
  // (se der pra surfacear via toast no futuro, melhor ainda).
  const persistMessage = useCallback((msg) => {
    if (!userId) return;
    authFetch(`${API_URL}/api/chat-history?user_id=${encodeURIComponent(userId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text: msg.text,
        isUser: msg.isUser,
        actions: msg.actions || [],
      }),
    })
      .then(async (r) => {
        if (!r.ok) {
          const body = await r.text().catch(() => "<sem body>");
          console.error(`[Nexo] persistMessage falhou ${r.status}: ${body.slice(0, 160)}`);
        }
      })
      .catch((err) => {
        console.error("[Nexo] persistMessage erro de rede:", err?.message || err);
      });
  }, [userId]);

  const handleActionConfirmations = useCallback((actions) => {
    const newConfirmations = processActionConfirmations(actions);
    newConfirmations.forEach((confirmation) => {
      setConfirmations(prev => [...prev, confirmation]);
      setTimeout(() => {
        setConfirmations(prev => prev.filter(c => c.id !== confirmation.id));
      }, CONFIRMATION_DISMISS_TIMEOUT);
    });
  }, []);

  // ── TTS-LATENCY-V7: streaming default LIGADO. Smokes E2E migrados pra
  //    SSE via captureSSEReply (frontend/e2e/helpers/sse_response_parser.js).
  //    Pra desligar em dev/testing: REACT_APP_STREAMING=false em frontend/.env.
  //
  //    Lógica:
  //      voiceConversationActive=true → stream (mantém ganho de pre-fetch V3+)
  //      REACT_APP_STREAMING explícito → respeita
  //      default → "true" (stream em texto puro também)
  //
  // BUGFIX-LATENCY-V3: voiceConversationActive força streaming on. O ganho
  // perceptivo da pre-fetch TTS depende do delta event chegar mid-stream.
  const _streamFlag = process.env.REACT_APP_STREAMING ?? "true";
  const STREAMING_ENABLED = _streamFlag !== "false" || voiceConversationActive;

  /**
   * sendMessageVanilla — endpoint POST /api/assistant/message, retorna
   * resposta completa de uma vez. Path padrão pré-S5B.
   */
  const sendMessageVanilla = useCallback(async (text, optimisticId, controller, origin) => {
    // BUGFIX-LATENCY-V2 — gera + propaga X-Request-ID. Mesmo id é stampado
    // em todos os events do backend pra essa request. Marca t0/t1/t2 pra
    // medir network_total e first_render no console.
    // VERSION-INDICATOR (item 4): origin "voice"|"text" ativa S7 no backend.
    const requestId = newRequestId();
    const _lat_t0 = performance.now();
    const body = { text, user_id: userId, sessionId };
    if (origin) body.origin = origin;
    const response = await authFetch(`${API_URL}/api/assistant/message`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
      requestId,
    });
    const _lat_t_resp = performance.now();
    if (!response.ok) {
      const err = new Error(`Failed to send message: ${response.status}`);
      err.statusCode = response.status;
      err.preStream = true; // sempre pre-completion no vanilla
      throw err;
    }
    const data = await response.json();
    const _lat_t1 = performance.now();
    const _network_ms = Math.round(_lat_t1 - _lat_t0);
    console.log("[latency] chat.network_total", {
      request_id: requestId,
      ms: _network_ms,
      ms_to_headers: Math.round(_lat_t_resp - _lat_t0),
    });
    trace.record("chat.network_total", {
      latency_ms: _network_ms,
      mode: "vanilla",
    }, { session_id: sessionId, request_id: requestId });

    if (typeof window !== "undefined" && window.__nexoVoiceT0) {
      const t7 = performance.now();
      console.log(`[nexo-voice] T7 reply text received (+${(t7 - window.__nexoVoiceT0).toFixed(0)}ms)`);
      console.log("[latency] voice.chat_response_received", {
        request_id: requestId,
        ms_since_voice_t0: Math.round(t7 - window.__nexoVoiceT0),
      });
    }

    // Bug 1 mitigation — TTS antes do setMessages
    if (voiceEnabled && data.reply_text) {
      speakResponse?.(data.reply_text);
    }

    const assistantMessage = {
      id: `srv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      text: data.reply_text,
      isUser: false,
      timestamp: new Date().toISOString(),
      status: "delivered",
      actions: data.actions || [],
      suggestions: data.suggestions || [],
      // DATACARDS-TEMPLATES — visual inline anexado pelo backend
      // (response.datacard) quando a pergunta matcha um tipo conhecido.
      datacard: data.datacard || null,
      // AUTO-SETUP-BACKWARDS-PLAN — quando projects_create casa template
      // (Sessão 2 offsets) e auto_setup NÃO disparou, backend anexa
      // {awaiting:'confirm_setup', project_id, ...} pra UI renderizar
      // <SchedulePreview> e iniciar o flow conversacional.
      project_setup: data.project_setup || null,
    };
    setMessages(prev => [
      ...prev.map(m => m.id === optimisticId ? { ...m, status: "delivered" } : m),
      assistantMessage,
    ]);
    const _lat_t2 = performance.now();
    const _first_render_ms = Math.round(_lat_t2 - _lat_t0);
    console.log("[latency] chat.first_render", {
      request_id: requestId,
      ms: _first_render_ms,
    });
    trace.record("chat.first_render", {
      latency_ms: _first_render_ms,
      mode: "vanilla",
      chars: (data.reply_text || "").length,
    }, { session_id: sessionId, request_id: requestId });
    persistMessage(assistantMessage);

    if (data.actions?.length > 0) {
      handleActionConfirmations(data.actions);
    }
    // SMART-AUTOMATIONS Etapa 7 — toast Undo quando auto-setup disparou
    if (data.auto_setup?.project_id && data.auto_setup.items_count > 0) {
      _showAutoSetupToast(data.auto_setup);
    }
    fetchDashboardData?.();
    return data;
  }, [userId, sessionId, voiceEnabled, speakResponse, fetchDashboardData, handleActionConfirmations, persistMessage]);

  /**
   * sendMessageStreaming — POST /api/assistant/message/stream.
   * Server-Sent Events: thinking → delta → done | error.
   *
   * Erros que indicam stream indisponível (statusCode 404/409/501-503,
   * network error ANTES do primeiro byte) marcam err.preStream=true →
   * caller tenta vanilla. Erros mid-stream NÃO retentam.
   */
  const sendMessageStreaming = useCallback(async (text, optimisticId, controller, origin) => {
    const t0 = performance.now();
    const requestId = newRequestId();
    const body = { text, user_id: userId, sessionId };
    if (origin) body.origin = origin;
    const response = await authFetch(`${API_URL}/api/assistant/message/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
      requestId,
    });

    if (!response.ok) {
      // Pre-stream error — fallback elegível
      const err = new Error(`stream HTTP ${response.status}`);
      err.statusCode = response.status;
      err.preStream = true;
      throw err;
    }
    if (!response.body) {
      const err = new Error("stream sem body");
      err.preStream = true;
      throw err;
    }

    const assistantId = `srv-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    let firstDeltaSeen = false;
    let receivedAny = false;
    let accumulated = "";
    let doneData = null;
    let errorEvent = null;

    // BUGFIX-LATENCY-V3 — pre-fetch TTS state. When voice is active, we
    // dispatch TTS for the first complete sentence as soon as it lands
    // mid-stream and queue the remainder on `done`. Audio starts playing
    // ~Xs before the agent finishes generating the rest of the reply.
    //
    // TTS-LATENCY-V4 — pre-fetch ativo sempre que voz esta habilitada
    // (toggle Settings). Antes exigia voiceConversationActive (overlay
    // aberto), o que bloqueava o ganho no fluxo TEXTO digitado com voz
    // habilitada — gerava ~10s de latencia entre texto aparecer no painel
    // e Nexo comecar a falar. Agora paridade com modo voz (~2s).
    const _prefetchEnabled = voiceEnabled && !!speakResponse;
    let _firstSentenceSent = false;
    let _prefetchSentChars = 0;
    // VOICE-POLISH-V8 Bug B — quando backend emite delta com is_stall=true,
    // dispara TTS imediato mas NÃO acumula em accumulated/reply_text. Flag
    // local _stallEmitted faz com que o primeiro speakResponse do real
    // delta use queue:true pra chainar atrás do stall em vez de cancelá-lo.
    let _stallEmitted = false;

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    const handleEvent = (eventType, data) => {
      receivedAny = true;
      if (eventType === "thinking") {
        setLoadingLabel("Consultando suas informações...");
      } else if (eventType === "delta") {
        const chunk = data?.text || "";
        // VOICE-POLISH-V8 Bug B — delta com is_stall=true é filler de TTS
        // (mascara latência de tool execution). NÃO acumula em accumulated,
        // NÃO cria/atualiza bubble de mensagem; apenas dispara speakResponse
        // imediato com queue:false e marca _stallEmitted pra subsequent
        // first-sentence usar queue:true (chain).
        if (data?.is_stall) {
          if (_prefetchEnabled) {
            try {
              speakResponse?.(chunk, { queue: false });
            } catch (e) {
              console.warn("[nexo-stream] stall TTS dispatch failed:", e);
            }
          }
          _stallEmitted = true;
          if (typeof window !== "undefined") {
            window.__lastStallAt = Date.now();
            window.__lastStallText = chunk;
          }
          console.log("[nexo-stream] stall_delta", { chars: chunk.length, text: chunk });
          return; // não acumula, não toca bubble
        }
        accumulated += chunk;
        if (!firstDeltaSeen) {
          firstDeltaSeen = true;
          // TELEMETRIA-S1 — primeiro delta real (não-stall) chegou.
          trace.record("chat.stream_first_delta", {
            ms_since_send: Math.round(performance.now() - t0),
          }, { session_id: sessionId, request_id: requestId });
          // Esconde loading label — texto streamando é o feedback
          setLoadingLabel("");
          // Append assistant message vazia (vai sendo preenchida)
          setMessages(prev => [
            ...prev.map(m => m.id === optimisticId ? { ...m, status: "delivered" } : m),
            {
              id: assistantId, text: chunk, isUser: false,
              timestamp: new Date().toISOString(),
              status: "streaming", actions: [],
            },
          ]);
          if (typeof window !== "undefined") {
            const ttft = performance.now() - t0;
            console.log(`[nexo-stream] TTFT ${ttft.toFixed(0)}ms`);
          }
        } else {
          setMessages(prev => prev.map(m =>
            m.id === assistantId ? { ...m, text: m.text + chunk } : m,
          ));
        }
        // Early TTS dispatch — see pickFirstSentenceForPrefetch in
        // _voiceUtils.js for the full rationale (regex carefully chosen
        // to avoid the chunk-boundary-mid-number bug from TTS-PRONUNCIA-FIX-3).
        if (_prefetchEnabled && !_firstSentenceSent) {
          const picked = pickFirstSentenceForPrefetch(accumulated);
          if (picked) {
            const firstSentence = picked.sentence;
            _prefetchSentChars = picked.endIndex;
            _firstSentenceSent = true;
            const _t_send = performance.now();
            console.log("[latency] voice.first_chunk_to_tts_request_sent", {
              request_id: requestId,
              chars: firstSentence.length,
              ms_since_chat_start: Math.round(_t_send - t0),
            });
            try {
              // V5 — passa o accumulated stream como fullContext pro echo
              // buffer capturar palavras alem do firstSentence dispatched.
              // V8 Bug B — se stall foi emitido, usa queue:true pra chainar
              // atrás do TTS de stall em vez de cancelá-lo via speakIdRef++.
              speakResponse(firstSentence, {
                fullContext: accumulated,
                queue: _stallEmitted,
              });
            } catch (e) {
              console.warn("[nexo-stream] early TTS dispatch failed:", e);
            }
          }
        }
      } else if (eventType === "done") {
        doneData = data;
      } else if (eventType === "error") {
        errorEvent = data;
      }
    };

    // SSE parser: split por \n\n, parse "event: X" + "data: Y"
    const flushBuffer = () => {
      const blocks = buffer.split("\n\n");
      // Último item pode ser parcial
      buffer = blocks.pop() || "";
      for (const block of blocks) {
        if (!block.trim()) continue;
        let eventType = null;
        const dataLines = [];
        for (const line of block.split("\n")) {
          if (line.startsWith("event: ")) eventType = line.slice(7).trim();
          else if (line.startsWith("data: ")) dataLines.push(line.slice(6));
        }
        if (eventType && dataLines.length > 0) {
          try {
            const data = JSON.parse(dataLines.join("\n"));
            handleEvent(eventType, data);
          } catch (e) {
            console.warn("[nexo-stream] failed to parse SSE data:", e);
          }
        }
      }
    };

    try {
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        flushBuffer();
      }
      // Process trailing buffer
      if (buffer.trim()) {
        buffer += "\n\n";
        flushBuffer();
      }
    } catch (e) {
      // Mid-stream network error: NÃO marca preStream → não retenta
      if (!receivedAny) {
        e.preStream = true;
      }
      throw e;
    }

    if (errorEvent) {
      const err = new Error(errorEvent.message || "agent error");
      err.code = errorEvent.code;
      err.preStream = false; // já recebemos eventos antes
      throw err;
    }

    if (!doneData) {
      const err = new Error("stream ended without 'done' event");
      err.preStream = false;
      throw err;
    }

    // Finalize: TTS antes do setMessages final (Bug 1 mitigation)
    const replyText = doneData.reply_text || accumulated;
    if (voiceEnabled && replyText) {
      // BUGFIX-LATENCY-V3 — pre-fetch handoff. If we already dispatched
      // the first sentence mid-stream, queue ONLY the remainder. Else
      // (no sentence boundary detected before done, or pre-fetch
      // disabled), fire the whole reply as before.
      if (_firstSentenceSent) {
        const remaining = replyText.slice(_prefetchSentChars).trim();
        if (remaining) {
          // V5 — fullContext: replyText inteiro garante que o echo buffer
          // tenha o texto completo (firstSentence + remaining).
          speakResponse?.(remaining, { queue: true, fullContext: replyText });
          console.log("[latency] voice.tts_remainder_queued", {
            request_id: requestId,
            remaining_chars: remaining.length,
          });
        }
      } else {
        // V8 Bug B — sem first-sentence boundary mid-stream mas stall
        // pode estar tocando; chainar via queue:true em vez de cancelar.
        speakResponse?.(replyText, { queue: _stallEmitted });
      }
    }
    const assistantFinal = {
      id: assistantId,
      text: replyText,
      isUser: false,
      timestamp: new Date().toISOString(),
      status: "delivered",
      actions: doneData.actions || [],
      suggestions: doneData.suggestions || [],
      // DATACARDS-TEMPLATES — propaga datacard do payload final
      // (o stream backend pode anexar no done event)
      datacard: doneData.datacard || null,
      // BUGFIX-AGENT-INTEGRATION — stream done agora carrega
      // project_setup e auto_setup; sem isso o SchedulePreview convite
      // (Sessão 3) e o toast Undo (Sessão 1) sumiam pra users no agent
      // streaming pipeline.
      project_setup: doneData.project_setup || null,
      auto_setup: doneData.auto_setup || null,
    };
    setMessages(prev => prev.map(m =>
      m.id === assistantId ? assistantFinal : m,
    ));
    persistMessage(assistantFinal);

    // TELEMETRIA-S1 — stream done com latency total + chars do reply.
    trace.record("chat.stream_done", {
      latency_ms: Math.round(performance.now() - t0),
      chars: (replyText || "").length,
    }, { session_id: sessionId, request_id: requestId });

    if (doneData.actions?.length > 0) {
      handleActionConfirmations(doneData.actions);
    }
    // SMART-AUTOMATIONS Etapa 7 (também no streaming) — toast Undo
    // quando auto-setup disparou. Sem isso, users no streaming pipeline
    // perdem a chance de desfazer.
    if (doneData.auto_setup?.project_id && doneData.auto_setup.items_count > 0) {
      _showAutoSetupToast(doneData.auto_setup);
    }
    fetchDashboardData?.();
    return {
      reply_text: replyText,
      actions: doneData.actions || [],
      suggestions: doneData.suggestions || [],
      project_setup: doneData.project_setup || null,
    };
  }, [userId, sessionId, voiceEnabled, speakResponse, fetchDashboardData, handleActionConfirmations, persistMessage]);

  const sendMessage = useCallback(async (text, options = {}) => {
    if (!text.trim() || isLoading) return;
    // VERSION-INDICATOR (item 4) — origin marca a origem do input:
    //   "voice" → STT/microfone (ativa gate S7 no backend pra ato destrutivo)
    //   "text"  → digitado (default; backend trata None como text)
    const origin = options.origin || null;

    // TELEMETRIA-S1 — chat.send. Hash do texto + len; SEM plain.
    // Mantém o sessionId visível ao recorder pra correlacionar
    // eventos subsequentes no mesmo turn.
    trace.setSessionId(sessionId);
    hashText(text).then((th) => {
      trace.record("chat.send", {
        text_hash: th,
        text_len: text.length,
        mode: "auto",
      }, { session_id: sessionId });
    }).catch(() => {});

    const optimisticId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const userMessage = {
      id: optimisticId,
      text,
      isUser: true,
      timestamp: new Date().toISOString(),
      status: "sending",
      actions: [],
    };
    setMessages(prev => [...prev, userMessage]);
    persistMessage(userMessage);
    setIsLoading(true);
    setLoadingLabel("Processando...");
    loadingTimersRef.current.forEach(clearTimeout);
    loadingTimersRef.current = [
      setTimeout(() => setLoadingLabel("Pensando..."), 3000),
      setTimeout(() => setLoadingLabel("Ainda tô aqui, só um instante..."), 15000),
    ];

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s — stream pode demorar

    try {
      // TTS-LATENCY-V7: queries com short-circuit no backend (datacard ou
      // briefing) preferem vanilla — esses caminhos retornam em ~100-500ms
      // sem LLM e mantém o ganho V5/V6. Stream pra todo o resto.
      // Detector em src/utils/shortCircuitDetector.js cobre patterns do
      // backend (services/datacard_templates + services/briefing_detector).
      const preferVanilla = STREAMING_ENABLED && shouldPreferVanilla(text);
      const useStream = STREAMING_ENABLED && !preferVanilla;
      if (useStream) {
        try {
          return await sendMessageStreaming(text, optimisticId, controller, origin);
        } catch (e) {
          if (e.preStream) {
            console.warn(`[nexo-stream] fallback pra vanilla: ${e.message}`);
            return await sendMessageVanilla(text, optimisticId, controller, origin);
          }
          throw e;
        }
      }
      return await sendMessageVanilla(text, optimisticId, controller, origin);
    } catch (error) {
      console.error("Error sending message:", error);
      const isTimeout = error.name === "AbortError";
      toast.error(isTimeout ? "Nexo demorou para responder. Tente novamente." : "Erro ao enviar mensagem");
      if (isTimeout) {
        const timeoutMsg = { text: "Desculpa, demorei demais. Pode repetir?", isUser: false, timestamp: new Date().toISOString(), actions: [] };
        setMessages(prev => [...prev, timeoutMsg]);
      }
    } finally {
      clearTimeout(timeoutId);
      setIsLoading(false);
      loadingTimersRef.current.forEach(clearTimeout);
      loadingTimersRef.current = [];
    }
  }, [isLoading, STREAMING_ENABLED, sendMessageStreaming, sendMessageVanilla, persistMessage]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (inputText.trim()) {
      sendMessage(inputText);
      setInputText("");
    }
  }, [inputText, sendMessage]);

  // "Nova conversa" / "Limpar" — limpa APENAS state local + localStorage.
  // Backend NÃO é mais deletado. UX antes era traiçoeira: clicar "Novo"
  // apagava permanentemente o histórico do user. Pra apagar mesmo o
  // histórico do backend será preciso ação separada com confirmação.
  const clearMessages = useCallback(async () => {
    setMessages([]);
    if (userId) localStorage.removeItem(STORAGE_KEY(userId));
  }, [userId]);

  return {
    messages,
    setMessages,
    isLoading,
    loadingLabel,
    inputText,
    setInputText,
    confirmations,
    sendMessage,
    handleSubmit,
    clearMessages,
  };
}
